import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Mail } from 'lucide-react';
import { login } from '../api/auth';

export function Login({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('admin@loopora.app');
  const [password, setPassword] = useState('Loopora123!');
  const [rememberMe, setRememberMe] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setErrorMsg('Please input valid email and password credentials.');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      await login(email, password);
      onLogin();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans text-gray-900">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center space-y-2">
        <img src="/brand/loopora-logo.png" alt="Loopora" className="mx-auto mb-2 size-16 rounded-2xl object-cover shadow-md" />
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Loopora</h1>
        <p className="text-sm text-gray-500 font-medium">Secure Multi-Workspace Publishing Platform</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 border border-gray-200 shadow-sm rounded-2xl sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {errorMsg && <div className="p-3 rounded-xl bg-red-50 text-red-700 text-xs font-semibold leading-relaxed border border-red-100">{errorMsg}</div>}

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Administrator Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-gray-400" size={16} />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-9 pr-3 py-2 border rounded-xl text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50/50" required />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Security Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400" size={16} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-9 pr-3 py-2 border rounded-xl text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50/50" required />
              </div>
              <div className="text-right mt-1.5"><a href="#" className="text-xs text-blue-600 hover:underline font-medium">Forgot security credentials?</a></div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input id="remember-me" type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="rounded border-gray-300 text-blue-600 size-3.5 focus:ring-blue-500 cursor-pointer" />
                <label htmlFor="remember-me" className="text-xs text-gray-500 font-medium cursor-pointer">Maintain active session cookie</label>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 py-2.5 text-sm font-bold text-white transition tracking-wide text-center disabled:opacity-50">
              {loading ? 'Signing In...' : 'Sign Into Loopora'}
            </button>
          </form>

          <div className="mt-6 border-t border-gray-150 pt-5 text-center space-y-3">
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest flex items-center justify-center gap-1.5"><ShieldCheck size={13} className="text-green-500" /> Secure publishing gateway</p>
            <p className="text-xs text-gray-500">
              By using Loopora, you agree to our <Link to="/terms" className="font-semibold text-blue-600 hover:underline">Terms of Service</Link> and acknowledge our <Link to="/privacy" className="font-semibold text-blue-600 hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
